import { memo, useCallback, useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Locate, Loader2, X } from 'lucide-react';
import { Button } from '../components/atoms/Button';
import { cn } from '../lib/utils';
import {
  acceptOperation,
  acceptOperations,
  rejectOperation,
  rejectOperations,
  scrollToOperation,
} from '../office/trackedChanges';
import {
  getReviewStatusesForMessage,
  setReviewStatus,
  setReviewStatusBulk,
} from '../office/editReviewStore';
import { readPendingAiEdits } from '../office/pendingChangesReader';
import type { EditOperation } from './types';

/**
 * Per-edit Accept/Reject cards with diff preview + bulk bar.
 *
 * **Source of truth is Word's own document.** On mount, we enumerate
 * AI-authored tracked changes directly via Office JS (see
 * `readPendingAiEdits`). No dependence on server-emitted edit metadata —
 * the DOCX already carries everything we need. Simpler, less coupled.
 *
 * Persisted status overrides (`accepted` / `rejected` from a prior session)
 * are layered on top via `getReviewStatusesForMessage`.
 */

interface Props {
  messageId: string;
  /**
   * Signal that the apply pipeline just finished on this message. When it
   * transitions to true, we refresh the tracked-change list. Lets the card
   * list update immediately after a new edit lands, without polling.
   */
  applyComplete: boolean;
}

export const EditOperationsReview = memo(function EditOperationsReviewImpl({
  messageId,
  applyComplete,
}: Props): JSX.Element | null {
  const [ops, setOps] = useState<EditOperation[]>([]);
  const [loading, setLoading] = useState(true);

  /** Load AI-authored tracked changes from the document; merge persisted status. */
  const refresh = useCallback(async () => {
    if (typeof Office === 'undefined') {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const pending = await readPendingAiEdits();
      const persisted = getReviewStatusesForMessage(messageId);
      const next = pending.map((op) => ({
        ...op,
        status: persisted[op.id] ?? op.status,
      }));
      setOps(next);
    } finally {
      setLoading(false);
    }
  }, [messageId]);

  // Initial load + re-load whenever the apply-complete signal flips.
  useEffect(() => {
    void refresh();
  }, [refresh, applyComplete]);

  // Re-check on window focus so if the user accepted changes via Word's
  // Review ribbon, our cards stay in sync.
  useEffect(() => {
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const handleAccept = useCallback(
    async (op: EditOperation) => {
      setOps((prev) => prev.map((o) => (o.id === op.id ? { ...o, status: 'accepted' } : o)));
      try {
        await acceptOperation(op);
        await setReviewStatus(messageId, op.id, 'accepted');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[EditOperations] accept failed', err);
        setOps((prev) => prev.map((o) => (o.id === op.id ? { ...o, status: 'pending' } : o)));
      }
    },
    [messageId],
  );

  const handleReject = useCallback(
    async (op: EditOperation) => {
      setOps((prev) => prev.map((o) => (o.id === op.id ? { ...o, status: 'rejected' } : o)));
      try {
        await rejectOperation(op);
        await setReviewStatus(messageId, op.id, 'rejected');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[EditOperations] reject failed', err);
        setOps((prev) => prev.map((o) => (o.id === op.id ? { ...o, status: 'pending' } : o)));
      }
    },
    [messageId],
  );

  const handleAcceptAll = useCallback(async () => {
    const pending = ops.filter((o) => o.status === 'pending');
    if (pending.length === 0) return;
    setOps((prev) =>
      prev.map((o) => (o.status === 'pending' ? { ...o, status: 'accepted' } : o)),
    );
    try {
      await acceptOperations(pending);
      await setReviewStatusBulk(messageId, pending.map((o) => o.id), 'accepted');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[EditOperations] accept-all failed', err);
    }
  }, [messageId, ops]);

  const handleRejectAll = useCallback(async () => {
    const pending = ops.filter((o) => o.status === 'pending');
    if (pending.length === 0) return;
    setOps((prev) =>
      prev.map((o) => (o.status === 'pending' ? { ...o, status: 'rejected' } : o)),
    );
    try {
      await rejectOperations(pending);
      await setReviewStatusBulk(messageId, pending.map((o) => o.id), 'rejected');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[EditOperations] reject-all failed', err);
    }
  }, [messageId, ops]);

  if (loading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
        <Loader2 className="w-3 h-3 animate-spin" />
        Reading AI edits…
      </div>
    );
  }
  if (ops.length === 0) return null;

  const pendingCount = ops.filter((o) => o.status === 'pending').length;

  return (
    <div className="mt-3 space-y-1.5">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">
          {ops.length} AI edit{ops.length === 1 ? '' : 's'} —{' '}
          {pendingCount === 0 ? 'all reviewed' : `${pendingCount} pending`}
        </span>
        {pendingCount > 0 && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px] gap-1 text-green-700 hover:bg-green-50"
              onClick={handleAcceptAll}
            >
              <Check className="w-3 h-3" />
              Accept all
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[11px] gap-1 text-red-700 hover:bg-red-50"
              onClick={handleRejectAll}
            >
              <X className="w-3 h-3" />
              Reject all
            </Button>
          </div>
        )}
      </div>

      {ops.map((op, idx) => (
        <EditCard
          key={op.id}
          op={op}
          index={idx + 1}
          onAccept={() => handleAccept(op)}
          onReject={() => handleReject(op)}
        />
      ))}
    </div>
  );
});

// --- Single edit card -------------------------------------------------------

interface EditCardProps {
  op: EditOperation;
  index: number;
  onAccept: () => void;
  onReject: () => void;
}

const EditCard = memo(function EditCardImpl({
  op,
  index,
  onAccept,
  onReject,
}: EditCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(op.status === 'pending');

  const statusStyle =
    op.status === 'accepted'
      ? 'border-green-200 bg-green-50/40'
      : op.status === 'rejected'
        ? 'border-red-200 bg-red-50/40 opacity-80'
        : 'border-border bg-background';

  return (
    <div className={cn('rounded-md border text-xs', statusStyle)}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-1.5 px-2 py-1.5 text-left"
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
        )}
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold flex-shrink-0">
          {index}
        </span>
        <span className="flex-1 min-w-0 text-[12px] text-foreground leading-snug truncate">
          {op.description}
        </span>
        <StatusBadge status={op.status} />
      </button>

      {expanded && (
        <div className="px-2 pb-2 space-y-1.5">
          <DiffDisplay op={op} />
          {op.status === 'pending' ? (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-[11px] gap-1 flex-1 text-green-700 border-green-200 hover:bg-green-50"
                onClick={onAccept}
              >
                <Check className="w-3 h-3" />
                Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-[11px] gap-1 flex-1 text-red-700 border-red-200 hover:bg-red-50"
                onClick={onReject}
              >
                <X className="w-3 h-3" />
                Reject
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-[11px] gap-1"
                onClick={() => void scrollToOperation(op)}
                title="Scroll document to this change"
              >
                <Locate className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <div className="text-[11px] text-muted-foreground italic">
              {op.status === 'accepted' ? 'Accepted' : 'Rejected'}. You can still change it from
              Word&apos;s Review ribbon.
            </div>
          )}
        </div>
      )}
    </div>
  );
});

function StatusBadge({ status }: { status: EditOperation['status'] }): JSX.Element | null {
  if (status === 'pending') return null;
  const isAccept = status === 'accepted';
  return (
    <span
      className={cn(
        'text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0',
        isAccept ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700',
      )}
    >
      {isAccept ? '✓ Accepted' : '✗ Rejected'}
    </span>
  );
}

function DiffDisplay({ op }: { op: EditOperation }): JSX.Element {
  const showOld = op.editType !== 'insert' && !!op.oldText;
  const showNew = op.editType !== 'delete' && !!op.newText;
  return (
    <div className="rounded border border-border/60 bg-muted/20 text-[11px] font-mono">
      {showOld && (
        <div className="px-2 py-1 border-b border-border/40 bg-red-50/30 text-red-900 whitespace-pre-wrap break-words">
          <span className="text-red-700 font-bold mr-1">−</span>
          {op.oldText}
        </div>
      )}
      {showNew && (
        <div className="px-2 py-1 bg-green-50/30 text-green-900 whitespace-pre-wrap break-words">
          <span className="text-green-700 font-bold mr-1">+</span>
          {op.newText}
        </div>
      )}
    </div>
  );
}
